import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RuleRow {
  readonly ruleConfigId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly ruleType: string;
  readonly status: string;
  readonly versionNo: number;
}

const RULE_TYPES = ['APPROVAL', 'VALIDATION', 'ROUTING', 'ESCALATION', 'ASSIGNMENT', 'CALCULATION', 'NOTIFICATION', 'COMPLIANCE'];
const STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

/**
 * M40 — Business Rule Designer (§11). The rule authoring surface over T527.
 * Define declarative business rules (condition → action) of any of the eight
 * kinds under an application. Viewing needs RULE_VIEW; authoring needs
 * RULE_MANAGE. Rules are versioned and evaluated by the platform runtime.
 */
export function BusinessRuleDesigner(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [ruleName, setRuleName] = useState('');
  const [ruleType, setRuleType] = useState('VALIDATION');
  const [condition, setCondition] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (typeFilter) params.set('ruleType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', '100');
    apiRequest<{ data: RuleRow[]; total: number }>(`/v1/app-platform/rules?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, typeFilter, statusFilter, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!ruleName.trim()) { setActionError(t('m40.ruleDesigner.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: RuleRow }>(`/v1/app-platform/rules`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), ruleName: ruleName.trim(), ruleType, condition: condition.trim() || undefined },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.ruleCode }));
      setRuleName('');
      setCondition('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.ruleDesigner.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: RuleRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/rules/${row.ruleConfigId}`, { method: 'DELETE', token });
      setNotice(t('m40.common.removed', { code: row.ruleCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.ruleDesigner.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="business-rule-designer">
      <h1>{t('m40.ruleDesigner.title')}</h1>

      <section aria-label="rule-scope">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
      </section>

      <section aria-label="create-rule">
        <h2>{t('m40.ruleDesigner.newRule')}</h2>
        <form aria-label="rule-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-rule-name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} /></label>
          <label>{t('m40.common.type')}
            <select aria-label="field-rule-type" value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
              {RULE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m40.ruleDesigner.condition')}<input aria-label="field-condition" value={condition} onChange={(e) => setCondition(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-rule" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.ruleDesigner.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="rule-error">{actionError}</p>}
        {notice && <p aria-label="rule-notice">{notice}</p>}
      </section>

      <section aria-label="rule-filters">
        <label>{t('m40.common.type')}
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">{t('m40.common.allTypes')}</option>
            {RULE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="rule-total">{t('m40.ruleDesigner.total', { count: rows.length })}</p>
          <DataTable<RuleRow>
            ariaLabel="rule-table"
            testId="rule-table"
            rowKey={(r) => r.ruleConfigId}
            rowDataAttrs={(r) => ({ 'data-rule': r.ruleCode })}
            emptyMessage={t('m40.ruleDesigner.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.ruleCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.ruleName },
              { key: 'type', header: t('m40.common.type'), render: (r) => r.ruleType },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.ruleCode}`, render: (r) => r.status },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <Button variant="danger" type="button" aria-label={`remove-${r.ruleCode}`} onClick={() => void remove(r)}>{t('m40.common.remove')}</Button>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
